import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { api, LEAGUES } from "@/lib/api";
import { GAMLY_PERSONALITIES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { PlayCircleIcon, TelevisionIcon, ArrowRightIcon, GearSixIcon } from "@phosphor-icons/react";
import TeamLogo from "@/components/TeamLogo";
import WorldSeriesBadge from "@/components/WorldSeriesBadge";

function WatchTile({ game }) {
  const home = game.home_score > game.away_score && game.status !== "UPCOMING";
  const away = game.away_score > game.home_score && game.status !== "UPCOMING";
  return (
    <Link
      to={game.is_demo ? `/games/${game.id}` : `/watch/${game.id}`}
      data-testid={`watch-tile-${game.id}`}
      className={`block bg-[#121212] border rounded-md overflow-hidden transition-colors group ${
        game.is_demo ? "border-[#FDB927]/40 hover:border-[#FDB927]/70" : "border-white/10 hover:border-[#0A84FF]/50"
      }`}
    >
      <div className="px-4 py-2.5 flex items-center justify-between border-b border-white/5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40">{game.league}</span>
          <WorldSeriesBadge game={game} compact />
          {game.is_demo && (
            <span data-testid="watch-demo-badge" className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#FDB927]/15 border border-[#FDB927]/45 text-[#FDB927] text-[9px] font-black uppercase tracking-[0.15em]">Demo</span>
          )}
          {game.tv && !game.postseason?.is_world_series && (
            <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white/40">
              <TelevisionIcon size={11} /> {game.tv}
            </span>
          )}
        </div>
        {game.status === "LIVE" ? (
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-[#FF0000]/15 text-[#FF453A] border border-[#FF453A]/40">
            <span className="live-dot" />
            <span className="text-[10px] font-black uppercase tracking-[0.15em]">{game.period_label}</span>
          </span>
        ) : (
          <span className="text-[10px] font-black uppercase tracking-[0.15em] text-white/55">{game.period_label}</span>
        )}
      </div>
      <div className="px-4 py-3">
        {[
          { abbr: game.away_team_abbr, name: game.away_team, score: game.away_score, color: game.away_team_color, logo: game.away_team_logo, win: away },
          { abbr: game.home_team_abbr, name: game.home_team, score: game.home_score, color: game.home_team_color, logo: game.home_team_logo, win: home },
        ].map((t) => (
          <div key={t.abbr + t.name} className="flex items-center justify-between py-1.5">
            <div className="flex items-center gap-3 min-w-0">
              <TeamLogo abbr={t.abbr} logo={t.logo} color={t.color} size={36} />
              <div className="font-display font-bold uppercase text-sm tracking-tight truncate text-white/80">{t.name}</div>
            </div>
            <div className={`font-num text-3xl font-black ${t.win ? "text-white" : "text-white/60"}`}>{t.score}</div>
          </div>
        ))}
        <div className="mt-3 text-[11px] text-white/50">
          <span className="font-bold uppercase tracking-wider text-white/40">Key matchup · </span>
          <span className="text-white/80 font-medium">{game.key_matchup}</span>
        </div>
      </div>
      <div className="px-4 pb-4">
        <span className={`w-full py-2.5 rounded-md font-black text-sm uppercase tracking-wide inline-flex items-center justify-center gap-2 transition-colors ${
          game.is_demo
            ? "bg-[#FDB927]/15 border border-[#FDB927]/50 text-[#FDB927] group-hover:bg-[#FDB927] group-hover:text-black"
            : "bg-[#0A84FF]/10 border border-[#0A84FF]/50 text-[#0A84FF] group-hover:bg-[#0A84FF] group-hover:text-white"
        }`}>
          <PlayCircleIcon weight="fill" /> {game.is_demo ? "Preview Live Pitch View" : "Watch With Gamly"} <ArrowRightIcon size={13} />
        </span>
      </div>
    </Link>
  );
}

export default function Watch() {
  const { user } = useAuth();
  const [league, setLeague] = useState("ALL");
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/games", { params: { league } })
      .then((r) => setGames(r.data))
      .catch(() => setGames([]))
      .finally(() => setLoading(false));
  }, [league]);

  const persona = GAMLY_PERSONALITIES.find((p) => p.value === (user?.personality || "broadcaster"));

  return (
    <AppLayout>
      <div className="mb-6 fade-up flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A84FF] mb-2">Live Companion</p>
          <h1 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tighter">Watch With Gamly</h1>
          <p className="text-sm text-white/55 mt-2">Pick a game and let Gamly call it live — fresh insight cards every 30 seconds.</p>
        </div>
        {persona && (
          <Link to="/settings" data-testid="watch-persona-chip" className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#121212] border border-white/10 hover:border-white/25 transition-colors">
            <persona.icon weight="bold" size={16} className="text-[#0A84FF]" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-white/80">{persona.label}</span>
            <GearSixIcon size={13} className="text-white/40" />
          </Link>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="watch-league-filter">
        {LEAGUES.map((lg) => (
          <button key={lg} onClick={() => setLeague(lg)} data-testid={`watch-league-${lg}`}
            className={`px-4 py-2 text-xs font-black uppercase tracking-[0.15em] rounded-md border transition-colors ${
              league === lg ? "border-[#0A84FF] bg-[#0A84FF]/10 text-[#0A84FF]" : "border-white/10 text-white/60 hover:text-white hover:border-white/25"
            }`}>{lg}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-white/60 text-sm" data-testid="watch-loading">Loading slate…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="watch-grid">
          {games.map((g) => <WatchTile key={g.id} game={g} />)}
          {games.length === 0 && <div className="text-white/50">No games for this league.</div>}
        </div>
      )}
    </AppLayout>
  );
}
